import { useState, useEffect, useRef, useMemo } from 'react';
import { theme } from '../../styles/theme';

/**
 * Mapillary street-level imagery thumbnail.
 * Shows a photo near the player's position, filtered by compass angle
 * to approximate the player's facing direction.
 *
 * Queries are debounced and cached to minimize API calls.
 * When the player rotates their vision cone, the best-matching photo
 * is picked from the cached set — no extra API call needed.
 *
 * If no Mapillary token is configured, the component renders nothing.
 */

// Mapillary API v4 — free, open street-level imagery
// Get a token at https://www.mapillary.com/developer
const MAPILLARY_TOKEN = import.meta.env.VITE_MAPILLARY_TOKEN as string | undefined;

interface Props {
  lat: number;
  lng: number;
  facing: number; // degrees, 0=north, clockwise
  isInBuilding?: boolean;
  onExpandChange?: (expanded: boolean) => void;
}

interface MapillaryImage {
  id: string;
  thumb_256_url: string;
  thumb_1024_url: string;
  computed_compass_angle: number;
  geometry: { coordinates: [number, number] };
}

// Cache: grid cell → array of ALL images at that location
const imageCache = new Map<string, { images: MapillaryImage[]; timestamp: number }>();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const GRID_SIZE = 0.0003; // ~33m grid cells for cache keys

function gridKey(lat: number, lng: number): string {
  const gLat = Math.round(lat / GRID_SIZE) * GRID_SIZE;
  const gLng = Math.round(lng / GRID_SIZE) * GRID_SIZE;
  return `${gLat.toFixed(5)},${gLng.toFixed(5)}`;
}

/** Angular distance (0-180) between two compass angles in degrees */
function angleDist(a: number, b: number): number {
  let d = Math.abs(a - b) % 360;
  if (d > 180) d = 360 - d;
  return d;
}

/** Meters between an image's captured point and the player position. */
function imageDistanceM(img: MapillaryImage, playerLat: number, playerLng: number): number {
  const [imgLng, imgLat] = img.geometry.coordinates;
  const dLat = (imgLat - playerLat) * 111000;
  const dLng = (imgLng - playerLng) * 111000 * Math.cos((playerLat * Math.PI) / 180);
  return Math.sqrt(dLat * dLat + dLng * dLng);
}

/**
 * Pick a Mapillary image to display given the player's position, facing,
 * and a set of candidates. Two-tier selection:
 *
 *  - If any candidates are within CLOSE_M of the player, treat them as the
 *    "here and now" set and pick the one whose compass angle best matches
 *    the player's facing (same behavior as before).
 *  - Otherwise, fall back to the nearest image overall (up to the bbox
 *    radius). This covers POIs that Mapillary hasn't imaged directly but
 *    are close to a street that has been.
 *
 * Returns the chosen image plus its distance in meters, so the view can
 * optionally annotate "nearest available ~Xm away".
 */
const CLOSE_M = 55;
function pickBestImage(
  images: MapillaryImage[],
  facing: number,
  playerLat: number,
  playerLng: number,
): { image: MapillaryImage | null; distanceM: number; isFallback: boolean } {
  if (images.length === 0) return { image: null, distanceM: 0, isFallback: false };

  const close = images.filter(img => imageDistanceM(img, playerLat, playerLng) <= CLOSE_M);

  if (close.length > 0) {
    let best: MapillaryImage = close[0];
    let bestDiff = angleDist(best.computed_compass_angle, facing);
    for (let i = 1; i < close.length; i++) {
      const diff = angleDist(close[i].computed_compass_angle, facing);
      if (diff < bestDiff) { bestDiff = diff; best = close[i]; }
    }
    return { image: best, distanceM: imageDistanceM(best, playerLat, playerLng), isFallback: false };
  }

  // Fallback: nearest image
  let best: MapillaryImage = images[0];
  let bestDist = imageDistanceM(best, playerLat, playerLng);
  for (let i = 1; i < images.length; i++) {
    const d = imageDistanceM(images[i], playerLat, playerLng);
    if (d < bestDist) { bestDist = d; best = images[i]; }
  }
  return { image: best, distanceM: bestDist, isFallback: true };
}

export function StreetViewThumbnail({ lat, lng, facing, isInBuilding, onExpandChange }: Props) {
  // Cached images for the current grid cell
  const [cachedImages, setCachedImages] = useState<MapillaryImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const lastFetchKey = useRef<string>('');
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Fetch images when position changes (debounced)
  useEffect(() => {
    if (!MAPILLARY_TOKEN) return;

    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      const key = gridKey(lat, lng);

      // Skip if we already fetched for this grid cell
      if (key === lastFetchKey.current) return;
      lastFetchKey.current = key;

      // Check cache
      const cached = imageCache.get(key);
      if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
        setCachedImages(cached.images);
        return;
      }

      // Abort previous in-flight request
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);

      // Query Mapillary for images near this position. Previously we used a
      // ~55m bbox and rendered "NO IMAGERY" whenever nothing was in range.
      // Broaden to ~100m so neighboring streets count as a fallback, then
      // let the distance-annotation in the view show how far the chosen
      // image is from the player's actual position.
      const delta = 0.0009; // ~100m bbox
      const bbox = `${lng - delta},${lat - delta},${lng + delta},${lat + delta}`;
      const url = `https://graph.mapillary.com/images?access_token=${MAPILLARY_TOKEN}&fields=id,thumb_256_url,thumb_1024_url,computed_compass_angle,geometry&bbox=${bbox}&limit=20`;

      fetch(url, { signal: controller.signal })
        .then(res => {
          if (!res.ok) throw new Error(`Mapillary API ${res.status}`);
          return res.json();
        })
        .then((data: { data: MapillaryImage[] }) => {
          if (controller.signal.aborted) return;
          const images = data.data ?? [];
          imageCache.set(key, { images, timestamp: Date.now() });
          setCachedImages(images);
          setLoading(false);
        })
        .catch(err => {
          if (err.name !== 'AbortError') {
            console.warn('[StreetView] Mapillary fetch failed:', err.message);
            imageCache.set(key, { images: [], timestamp: Date.now() });
            setCachedImages([]);
            setLoading(false);
          }
        });
    }, 400);

    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [lat, lng]); // only re-fetch on position change, NOT facing

  // Pick best image reactively when facing or cached images change.
  // Selection now knows about the player's position so it can fall back
  // to "nearest available" when nothing was imaged right at the POI.
  const bestImageInfo = useMemo(() => {
    return pickBestImage(cachedImages, facing, lat, lng);
  }, [cachedImages, facing, lat, lng]);

  const imageUrl = bestImageInfo.image?.thumb_1024_url ?? null;

  // Don't render anything if no token configured
  if (!MAPILLARY_TOKEN) return null;

  // Don't show when inside a building
  if (isInBuilding) return null;

  const size = expanded ? 280 : 140;
  const height = expanded ? 200 : 100;

  return (
    <div
      style={{
        position: 'absolute',
        top: '120px',
        left: '10px',
        width: `${size}px`,
        height: `${height}px`,
        borderRadius: '6px',
        overflow: 'hidden',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        background: 'rgba(0, 0, 0, 0.6)',
        cursor: 'pointer',
        zIndex: 10,
        transition: 'width 0.2s ease, height 0.2s ease',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.4)',
      }}
      onClick={() => {
        const next = !expanded;
        setExpanded(next);
        onExpandChange?.(next);
      }}
      title={expanded ? 'Click to minimize' : 'Click to expand'}
    >
      {imageUrl ? (
        <img
          src={imageUrl}
          alt="Street view"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
          }}
        />
      ) : (
        <div style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: theme.fonts.mono,
          fontSize: '10px',
          color: 'rgba(255, 255, 255, 0.3)',
          letterSpacing: '1px',
        }}>
          {loading ? 'LOADING...' : 'NO IMAGERY'}
        </div>
      )}
      {/* Direction indicator */}
      <div style={{
        position: 'absolute',
        bottom: '4px',
        left: '4px',
        padding: '1px 5px',
        background: 'rgba(0, 0, 0, 0.6)',
        borderRadius: '3px',
        fontFamily: theme.fonts.mono,
        fontSize: '9px',
        color: 'rgba(255, 255, 255, 0.5)',
        letterSpacing: '0.5px',
      }}>
        {Math.round(facing)}°
      </div>
      {/* Fallback distance badge — only when the chosen image is outside
          the "close" window (nothing right at the player). Tells the
          player the view isn't exactly where they are. */}
      {bestImageInfo.isFallback && imageUrl && (
        <div style={{
          position: 'absolute',
          top: '4px',
          left: '4px',
          padding: '1px 5px',
          background: 'rgba(255, 170, 68, 0.18)',
          border: '1px solid rgba(255, 170, 68, 0.4)',
          borderRadius: '3px',
          fontFamily: theme.fonts.mono,
          fontSize: '9px',
          color: '#ffcc88',
          letterSpacing: '0.5px',
        }}>
          ~{Math.round(bestImageInfo.distanceM)}m away
        </div>
      )}
      {/* Mapillary attribution */}
      <div style={{
        position: 'absolute',
        bottom: '4px',
        right: '4px',
        padding: '1px 4px',
        background: 'rgba(0, 0, 0, 0.6)',
        borderRadius: '3px',
        fontFamily: theme.fonts.mono,
        fontSize: '8px',
        color: 'rgba(255, 255, 255, 0.35)',
      }}>
        Mapillary
      </div>
    </div>
  );
}
